import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { ticketsService } from 'views/services/ticketsService';
import { SupportTicketCategory } from 'views/types/tickets';
import * as Dialog from '@radix-ui/react-dialog';
import { FaPlus } from 'react-icons/fa';
import { Button } from 'components/button';
import { IoIosCloseCircle } from 'react-icons/io';
import { ReadPermissionGate } from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { usePermissions } from 'hooks/usePermissions';

type MetadataItem = { key: string; value: string };

type FormValues = {
  userId: string;
  message: string;
  category: SupportTicketCategory;
  metadata?: MetadataItem[];
};

export const SupportTicketForm = () => {
  const [open, setOpen] = useState(false);
  const { register, handleSubmit, control, reset } = useForm<FormValues>({
    defaultValues: {
      metadata: [{ key: '', value: '' }],
    },
  });

  const { canUpdate } = usePermissions();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'metadata',
  });

  const onSubmit = async (data: FormValues) => {
    const metadataObject = data.metadata?.reduce(
      (acc, { key, value }) => {
        if (key) acc[key] = value;
        return acc;
      },
      {} as Record<string, unknown>
    );

    const output = {
      userId: data.userId,
      message: data.message,
      category: data.category,
      metadata: metadataObject,
    };

    const response = await ticketsService.createTicket(output);

    if (response) {
      reset();
      setOpen(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <ReadPermissionGate
        resource={PERMISSION_RESOURCES.CREATE_SUPPORT_TICKET_CTA}
      >
        <Dialog.Trigger asChild>
          <Button
            disabled={
              !canUpdate(PERMISSION_RESOURCES.SUPPORT_TICKETS) ||
              !canUpdate(PERMISSION_RESOURCES.CREATE_SUPPORT_TICKET_CTA)
            }
          >
            <FaPlus /> Create Support Ticket
          </Button>
        </Dialog.Trigger>
      </ReadPermissionGate>

      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-overlayShow fixed inset-0 z-50 bg-gray-800 bg-opacity-50" />
        <Dialog.Content
          onInteractOutside={e => {
            if ((e.target as HTMLElement).closest('.chakra-toast')) {
              e.preventDefault();
            }
          }}
          className="data-[state=open]:animate-contentShow fixed left-[50%] top-[50%] z-[60] h-[85vh] max-h-[85vh] w-[90vw] max-w-[90vw] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-white p-[25px] shadow-lg focus:outline-none dark:bg-gray-800 dark:text-white overflow-y-auto"
        >
          <div className="absolute top-2 right-2">
            <Dialog.Close asChild>
              <button
                className="text-gray-500 border-none hover:text-gray-700"
                onClick={() => {
                  reset();
                  setOpen(false);
                }}
              >
                <IoIosCloseCircle size={24} />
              </button>
            </Dialog.Close>
          </div>

          <Dialog.Title className="text-center text-lg font-medium mb-6">
            Ticket Creating
          </Dialog.Title>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label>Client ID</label>
              <input
                {...register('userId', { required: true })}
                className="dark:bg-gray-800 border p-1 w-full"
              />
            </div>

            <div>
              <label>Message</label>
              <textarea
                {...register('message', { required: true })}
                className="dark:bg-gray-800 border p-1 w-full"
              />
            </div>

            <div>
              <label>Category</label>
              <select
                {...register('category', { required: true })}
                className="dark:bg-gray-800 border p-1 w-full"
              >
                {Object.entries(SupportTicketCategory).map(([, value]) => (
                  <option
                    className="dark:bg-gray-800"
                    key={value}
                    value={value}
                  >
                    {value.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label>Metadata</label>
              {fields.map((field, index) => (
                <div key={field.id} className="flex gap-2 mb-2">
                  <input
                    {...register(`metadata.${index}.key` as const)}
                    placeholder="Key"
                    className="dark:bg-gray-800 border p-1 w-1/2"
                  />
                  <input
                    {...register(`metadata.${index}.value` as const)}
                    placeholder="Value"
                    className="dark:bg-gray-800 border p-1 w-1/2"
                  />
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="text-red-500"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => append({ key: '', value: '' })}
                className="text-blue-500"
              >
                + Add Metadata
              </button>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded"
              >
                Submit
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
